import { Icon as IconifyIcon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Menu } from '@mantine/core';
import type { KeyboardEvent } from 'react';
import {
  AutomationTriggerType,
  type AutomationWorkflow,
} from '../../../../api/automations-workflows-client/automations-workflows-client.type';
import { Icon } from '../../../../ui-library/icon/icon';
import { Loader } from '../../../../ui-library/loader/loader';
import { Text } from '../../../../ui-library/typography/typography';
import {
  getActionLabel,
  getTriggerDescription,
  getTriggerIcon,
  summarizeDeliveryChannels,
} from './agent-automations.helpers';

interface AgentAutomationCardProps {
  workflow: AutomationWorkflow;
  isBusy?: boolean;
  onOpen: (workflow: AutomationWorkflow) => void;
  onEdit: (workflow: AutomationWorkflow) => void;
  onToggleActive: (workflow: AutomationWorkflow) => void;
  onRunNow: (workflow: AutomationWorkflow) => void;
  onDelete: (workflow: AutomationWorkflow) => void;
}

export function AgentAutomationCard({
  workflow,
  isBusy,
  onOpen,
  onEdit,
  onToggleActive,
  onRunNow,
  onDelete,
}: AgentAutomationCardProps) {
  const canRunManually = workflow.trigger_type !== AutomationTriggerType.EVENT && workflow.is_active;
  const triggerDescription = getTriggerDescription(workflow);
  const triggerIcon = getTriggerIcon(workflow);
  const actionLabel = getActionLabel(workflow.action_type);
  const deliverySummary = summarizeDeliveryChannels(workflow.delivery_channels);
  const statePill = workflow.is_active
    ? { color: 'var(--primary-base)', background: 'rgba(35, 193, 107, 0.12)', label: 'Active' }
    : { color: 'var(--ink-base)', background: 'var(--sky-lighter)', label: 'Paused' };

  const handleOpen = (): void => onOpen(workflow);
  const handleEdit = (): void => onEdit(workflow);
  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpen();
    }
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-label={`View runs for automation ${workflow.name}`}
      onClick={handleOpen}
      onKeyDown={handleCardKeyDown}
    >
      <Body>
        <NameRow>
          <Text size="small" weight="bold" color="var(--ink-darkest)">
            {workflow.name}
          </Text>
        </NameRow>
        {workflow.description && (
          <Text size="small" color="var(--ink-base)">
            {workflow.description}
          </Text>
        )}
        <MetaRow>
          <MetaItem>
            <Icon name={triggerIcon} color="var(--ink-lighter)" size={14} />
            <Text size="tiny" color="var(--ink-lighter)">
              {triggerDescription}
            </Text>
          </MetaItem>
          <MetaDot>·</MetaDot>
          <Text size="tiny" color="var(--ink-lighter)">
            {actionLabel}
          </Text>
          <MetaDot>·</MetaDot>
          <Text size="tiny" color="var(--ink-lighter)">
            {deliverySummary}
          </Text>
        </MetaRow>
      </Body>

      <Actions onClick={(event) => event.stopPropagation()}>
        <StatusPill style={{ color: statePill.color, background: statePill.background }}>
          <Text size="tiny" weight="medium" color="inherit">
            {statePill.label}
          </Text>
        </StatusPill>
        <Menu shadow="sm" radius="md" position="bottom-end" offset={4} withinPortal={false}>
          <Menu.Target>
            <MoreButton aria-label="More options" disabled={isBusy}>
              {isBusy ? (
                <Loader size="xs" />
              ) : (
                <IconifyIcon icon="material-symbols:more-horiz" width={16} height={16} />
              )}
            </MoreButton>
          </Menu.Target>
          <Menu.Dropdown>
            {canRunManually && (
              <Menu.Item
                leftSection={<IconifyIcon icon="material-symbols:play-arrow-outline" width={14} height={14} />}
                onClick={() => onRunNow(workflow)}
                style={{ fontSize: '13px' }}
              >
                Run now
              </Menu.Item>
            )}
            <Menu.Item
              leftSection={<IconifyIcon icon="material-symbols:edit-outline" width={14} height={14} />}
              onClick={handleEdit}
              style={{ fontSize: '13px' }}
            >
              Edit
            </Menu.Item>
            <Menu.Item
              leftSection={
                <IconifyIcon
                  icon={workflow.is_active ? 'material-symbols:pause-outline' : 'material-symbols:play-circle-outline'}
                  width={14}
                  height={14}
                />
              }
              onClick={() => onToggleActive(workflow)}
              style={{ fontSize: '13px' }}
            >
              {workflow.is_active ? 'Pause' : 'Resume'}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              leftSection={<IconifyIcon icon="material-symbols:delete-outline" width={14} height={14} />}
              color="red"
              onClick={() => onDelete(workflow)}
              style={{ fontSize: '13px' }}
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Actions>
    </Card>
  );
}

const Card = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  transition: border-color 0.15s, background 0.15s;
  gap: 12px;
  cursor: pointer;

  &:hover {
    border-color: var(--sky-base);
    background: var(--sky-lightest);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 10px;
  line-height: 1;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const MetaDot = styled.span`
  color: var(--ink-lighter);
  font-size: 10px;
  line-height: 1;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const MoreButton = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--ink-lighter);
  transition: background 0.12s, color 0.12s;

  &:hover:not(:disabled) {
    background: var(--sky-lighter);
    color: var(--ink-dark);
  }

  &:disabled {
    cursor: default;
  }
`;

export type { AgentAutomationCardProps };
